<script setup lang="ts">

import type { ICarbucksProfile } from '@domain/Carbucks';
import { ref } from 'vue';
import { isDarkMode } from '@services/Theme';

type Props =
{
    canDismiss: boolean,
    showWhyLinkCta?: boolean
};

const model = defineModel<ICarbucksProfile>( { required: true } );
const { canDismiss, showWhyLinkCta = false } = defineProps<Props>();
const dismissedSetting = localStorage.getItem( 'dismissLinkedCardPromo' );
const isDismissed = ref( canDismiss ? dismissedSetting == 'true' : false );

/**
 * Dismisses the large format tile and replaces it with the small format
 * @param value 
 */
function dismiss( value: boolean )
{
    localStorage.setItem( 'dismissLinkedCardPromo', String( value ) );
    isDismissed.value = value;
}

</script>

<template>

    <div v-if="model.isLoaded && isDismissed == false" class="text-white">
        <img v-if="isDarkMode"
             src="@/assets/images/carbucks/link-card-banner-background-dark.png" 
             class="w-full h-full absolute left-0 top-0 opacity-80 object-cover" />
        <img v-else
             src="@/assets/images/carbucks/link-card-banner-background.png" 
             class="w-full h-full absolute left-0 top-0 object-cover" />
        <div class="p-4 lg:px-6 lg:pb-5 3xl:pb-7 tracking-wider">
            <h1 class="text-base lg:text-xl font-bold">
                Link your cards to earn cashback
            </h1>
            <p class="text-sm lg:text-base py-2">
                Unlock exclusive benefits and rewards<br/>
                Linked cards earn cashback<br/>
                rewards from our partners.
            </p>
            <div class="mt-2 flex items-center">
                <a :href="model.getLinkCardUrl()">
                    <Button :color="isDarkMode ? 'secondary' : 'primary'"
                            size="large"
                            class="flex items-center !border border-secondary-600 !ring-[6px] !ring-white/20 !rounded-lg">
                        Link payment card
                        <Icon name="arrow-right" class="ml-2 text-2xl" />
                    </Button>                
                </a>
                <Button v-if="canDismiss"
                        variant="text"
                        color="white"
                        @click="() => dismiss( true )">
                    Dismiss
                </Button>
            </div> 
            <RouterLink v-if="showWhyLinkCta"
                        to="/carbucks"
                        class="text-orange-900 mt-4 -mb-1 block pl-2">
                Why do I need to link a card?
            </RouterLink>
        </div>
    </div>

    <div v-else-if="model.isLoaded"
         class="h-14 bg-promo flex justify-between items-center px-2 lg:pl-3 lg:pr-2">
        <h1 class="text-base lg:text-lg text-white"
            @click="() => dismiss( false )">
            Link cards to earn cashback
        </h1>
        <a :href="model.getLinkCardUrl()">
            <Button :color="isDarkMode ? 'secondary' : 'primary'"
                    class="flex items-center">
                Link now
            </Button>                
        </a>
    </div>

</template>
